/*
 * Product photo viewer (Patrick, 2026-10-21). Replaces Dawn's lightbox, which stacked every photo
 * full width in one long scrolling page with only a close button.
 *
 * One photo at a time, opened on the photo that was clicked: "3 / 10" count, arrows, a thumbnail
 * strip, keyboard arrows and swipe. Click (mouse) or double-tap (touch) zooms 2.5x on that spot and
 * loads the full-size file; the zoomed photo follows the mouse, or drags under a finger. Pinch zooms
 * on phones. Esc, the close button, a click beside the photo or the phone's back button closes it.
 * Follows Baymard's product-gallery research (one image in focus, thumbnails, tap + pinch zoom).
 *
 * The modal markup (snippets/product-media-modal.liquid) is unchanged, so product-info.js can keep
 * swapping .product-media-modal__content on a variant change: slides are read again on every open.
 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.classList.add('spid-viewer');
        this.dialog = this.querySelector('[role="dialog"]');
        this.content = this.querySelector('.product-media-modal__content');
        this.index = 0;
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.pointers = new Map();
        this.buildChrome();
        this.bindStage();
        window.addEventListener('popstate', () => {
          if (this.historyPushed && this.hasAttribute('open')) {
            this.historyPushed = false;
            this.close();
          }
        });
      }

      buildChrome() {
        const chevron = (d) =>
          `<svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/></svg>`;
        this.count = document.createElement('p');
        this.count.className = 'spid-viewer__count';
        this.count.setAttribute('aria-live', 'polite');
        this.prevButton = document.createElement('button');
        this.prevButton.type = 'button';
        this.prevButton.className = 'spid-viewer__arrow spid-viewer__arrow--prev';
        this.prevButton.setAttribute('aria-label', 'Previous photo');
        this.prevButton.innerHTML = chevron('M15 5l-7 7 7 7');
        this.nextButton = document.createElement('button');
        this.nextButton.type = 'button';
        this.nextButton.className = 'spid-viewer__arrow spid-viewer__arrow--next';
        this.nextButton.setAttribute('aria-label', 'Next photo');
        this.nextButton.innerHTML = chevron('M9 5l7 7-7 7');
        this.thumbs = document.createElement('div');
        this.thumbs.className = 'spid-viewer__thumbs';
        this.thumbs.setAttribute('role', 'group');
        this.thumbs.setAttribute('aria-label', 'Product photos');
        this.prevButton.addEventListener('click', () => this.go(this.index - 1));
        this.nextButton.addEventListener('click', () => this.go(this.index + 1));
        this.dialog.prepend(this.count);
        this.dialog.append(this.prevButton, this.nextButton, this.thumbs);
        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
          else if (event.key === 'ArrowRight') this.go(this.index + 1);
          else return;
          event.preventDefault();
        });
      }

      // Variant photos the gallery hides (hide_variants) stay hidden here too; the selected
      // variant's photo is always the first child, as in Dawn.
      readSlides() {
        return Array.from(this.content.children).filter(
          (el, i) => el.hasAttribute('data-media-id') || el.querySelector('[data-media-id]')
            ? !(i > 0 && el.classList.contains('product__media-item--variant'))
            : false
        );
      }

      mediaId(el) {
        return el.getAttribute('data-media-id') || el.querySelector('[data-media-id]')?.getAttribute('data-media-id');
      }

      // Shopify CDN URLs take a width parameter; thumbnails ask for a small file.
      sized(url, width) {
        if (!url) return '';
        const u = new URL(url, location.href);
        u.searchParams.set('width', width);
        u.searchParams.delete('height');
        u.searchParams.delete('crop');
        return u.toString();
      }

      fullSize(img) {
        const entries = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim()).filter(Boolean);
        const last = entries[entries.length - 1];
        return last ? last.split(/\s+/)[0] : img.src;
      }

      show(opener) {
        super.show(opener);
        document.documentElement.classList.add('spid-viewer-open');
        this.slides = this.readSlides();
        const id = opener?.getAttribute('data-media-id');
        const start = Math.max(0, this.slides.findIndex((el) => this.mediaId(el) === id));
        this.renderThumbs();
        this.go(start, true);
        if (!this.historyPushed) {
          history.pushState({ spidViewer: true }, '');
          this.historyPushed = true;
        }
      }

      // Every way out goes through history so the phone's back button and the close button agree.
      hide() {
        if (this.historyPushed) {
          this.historyPushed = false;
          history.back();
        }
        this.close();
      }

      close() {
        if (!this.hasAttribute('open')) return;
        this.resetZoom();
        document.documentElement.classList.remove('spid-viewer-open');
        super.hide();
      }

      renderThumbs() {
        this.thumbs.innerHTML = '';
        this.thumbs.hidden = this.slides.length < 2;
        this.prevButton.hidden = this.nextButton.hidden = this.slides.length < 2;
        this.slides.forEach((slide, i) => {
          const img = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'spid-viewer__thumb';
          button.setAttribute('aria-label', `Show photo ${i + 1} of ${this.slides.length}`);
          if (img) {
            const thumb = document.createElement('img');
            thumb.src = this.sized(img.getAttribute('src'), 160);
            thumb.alt = '';
            thumb.width = 64;
            thumb.height = 64;
            thumb.loading = 'lazy';
            button.append(thumb);
          }
          if (slide.tagName !== 'IMG') button.classList.add('spid-viewer__thumb--media');
          button.addEventListener('click', () => this.go(i));
          this.thumbs.append(button);
        });
      }

      go(i, opening) {
        if (!this.slides?.length) return;
        const n = this.slides.length;
        this.index = (i + n) % n;
        this.resetZoom();
        if (!opening) window.pauseAllMedia();
        this.slides.forEach((el, k) => el.classList.toggle('active', k === this.index));
        this.content.querySelectorAll(':scope > *').forEach((el) => {
          if (!this.slides.includes(el)) el.classList.remove('active');
        });
        const active = this.slides[this.index];
        if (active.tagName === 'IMG') {
          active.loading = 'eager';
          active.sizes = '100vw';
        }
        this.content.classList.toggle('is-image', active.tagName === 'IMG');
        const content = active.querySelector?.('template')?.content;
        if (active.nodeName === 'DEFERRED-MEDIA' && content?.querySelector('.js-youtube')) active.loadContent();
        this.count.textContent = n > 1 ? `${this.index + 1} / ${n}` : '';
        Array.from(this.thumbs.children).forEach((b, k) => {
          b.classList.toggle('is-active', k === this.index);
          if (k === this.index) {
            b.setAttribute('aria-current', 'true');
            b.scrollIntoView({ block: 'nearest', inline: 'center' });
          } else b.removeAttribute('aria-current');
        });
      }

      activeImage() {
        const el = this.slides?.[this.index];
        return el && el.tagName === 'IMG' ? el : null;
      }

      resetZoom() {
        this.zoom = 1;
        this.panX = this.panY = 0;
        this.content.classList.remove('is-zoomed');
        this.slides?.forEach((el) => {
          if (el.tagName === 'IMG') el.style.transform = '';
        });
      }

      // Pan limits: the zoomed photo never leaves a gap at its own edges.
      clampPan() {
        const img = this.activeImage();
        if (!img) return;
        const maxX = (img.offsetWidth * (this.zoom - 1)) / 2;
        const maxY = (img.offsetHeight * (this.zoom - 1)) / 2;
        this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
        this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
      }

      apply() {
        const img = this.activeImage();
        if (!img) return;
        this.clampPan();
        img.style.transform = this.zoom > 1 ? `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})` : '';
        this.content.classList.toggle('is-zoomed', this.zoom > 1);
      }

      // Put the point under the cursor (as a fraction of the photo) in view.
      panTo(clientX, clientY) {
        const img = this.activeImage();
        const r = img.getBoundingClientRect();
        const w = r.width / this.zoom, h = r.height / this.zoom;
        const left = r.left + r.width / 2 - w / 2, top = r.top + r.height / 2 - h / 2;
        const fx = Math.max(0, Math.min(1, (clientX - left) / w));
        const fy = Math.max(0, Math.min(1, (clientY - top) / h));
        this.panX = (0.5 - fx) * w * (this.zoom - 1);
        this.panY = (0.5 - fy) * h * (this.zoom - 1);
        this.apply();
      }

      zoomAt(clientX, clientY, level) {
        const img = this.activeImage();
        if (!img) return;
        const full = this.fullSize(img);
        if (full && img.dataset.full !== full) {
          img.dataset.full = full;
          const loader = new Image();
          loader.onload = () => {
            img.removeAttribute('srcset');
            img.src = full;
          };
          loader.src = full;
        }
        const r = img.getBoundingClientRect();
        const fx = (clientX - r.left) / r.width, fy = (clientY - r.top) / r.height;
        this.zoom = level;
        this.panX = (0.5 - fx) * img.offsetWidth * (level - 1);
        this.panY = (0.5 - fy) * img.offsetHeight * (level - 1);
        this.apply();
      }

      bindStage() {
        const stage = this.content;
        let down = null, lastTap = 0, pinch = null;
        // Dawn closes the media modal on any mouse pointerup (a listener on the modal itself);
        // stopping the event at the dialog lets this viewer decide instead.
        this.dialog.addEventListener('pointerup', (event) => event.stopPropagation());

        stage.addEventListener('pointerdown', (event) => {
          if (!this.activeImage()) return;
          this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (this.pointers.size === 2) {
            const [a, b] = [...this.pointers.values()];
            pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.zoom };
            down = null;
            return;
          }
          down = { x: event.clientX, y: event.clientY, panX: this.panX, panY: this.panY, t: Date.now(), type: event.pointerType };
        });

        stage.addEventListener('pointermove', (event) => {
          if (this.pointers.has(event.pointerId)) this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (pinch && this.pointers.size === 2) {
            const [a, b] = [...this.pointers.values()];
            const level = Math.max(1, Math.min(4, (pinch.zoom * Math.hypot(a.x - b.x, a.y - b.y)) / pinch.dist));
            if (this.zoom === 1 && level > 1) this.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, level);
            else {
              this.zoom = level;
              this.apply();
            }
            return;
          }
          if (event.pointerType === 'mouse') {
            if (this.zoom > 1) this.panTo(event.clientX, event.clientY);
            return;
          }
          if (down && this.zoom > 1) {
            this.panX = down.panX + (event.clientX - down.x);
            this.panY = down.panY + (event.clientY - down.y);
            this.apply();
          }
        });

        const end = (event) => {
          this.pointers.delete(event.pointerId);
          if (pinch) {
            if (this.pointers.size < 2) pinch = null;
            if (this.zoom < 1.05) this.resetZoom();
            return;
          }
          if (!down) return;
          const dx = event.clientX - down.x, dy = event.clientY - down.y, moved = Math.hypot(dx, dy);
          const start = down;
          down = null;
          if (event.type === 'pointercancel') return;
          const onPhoto = event.target === this.activeImage();
          if (start.type === 'mouse') {
            if (moved > 6) return;
            if (!onPhoto && this.zoom === 1) return this.hide();
            if (this.zoom > 1) this.resetZoom();
            else this.zoomAt(event.clientX, event.clientY, 2.5);
            return;
          }
          // Touch: swipe between photos at normal size; double-tap zooms in and out.
          if (this.zoom === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) return this.go(this.index + (dx < 0 ? 1 : -1));
          if (moved > 10) return;
          const now = Date.now();
          if (now - lastTap < 300) {
            lastTap = 0;
            if (this.zoom > 1) this.resetZoom();
            else if (onPhoto) this.zoomAt(event.clientX, event.clientY, 2.5);
          } else lastTap = now;
        };
        stage.addEventListener('pointerup', end);
        stage.addEventListener('pointercancel', end);
        // Clicks beside a video or 3D model (not an image slide) still close the viewer.
        stage.addEventListener('click', (event) => {
          if (!this.activeImage() && event.target === stage) this.hide();
        });
      }
    }
  );
}
